/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { API_BASE_URL } from '@/lib/constants'
import { getAccessToken, getRefreshToken, setTokens, clearTokens, shouldRefreshToken } from '@/lib/token'
import type { TokenResponse } from '@/types/auth.types'

// Vytvoření Axios instance
export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
})

// Flag pro prevenci vícenásobného refresh requestu
let isRefreshing = false
let failedQueue: Array<{
    resolve: (token: string) => void
    reject: (error: unknown) => void
}> = []

const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error)
        } else if (token) {
            prom.resolve(token)
        }
    })

    failedQueue = []
}

// Request interceptor - přidá JWT token do hlavičky
apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        const accessToken = getAccessToken()

        // Pokud je to refresh endpoint, nepřidávej access token
        if (config.url?.includes('/auth/refresh')) {
            return config
        }

        // Pokud máme access token, zkontroluj zda by měl být obnoven
        if (accessToken && shouldRefreshToken(accessToken)) {
            const refreshToken = getRefreshToken()

            if (!refreshToken) {
                clearTokens()
                window.location.href = '/login'
                return Promise.reject(new Error('No refresh token available'))
            }

            // Pokud už probíhá refresh, počkej na něj
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                })
                    .then((token) => {
                        config.headers.Authorization = `Bearer ${token}`
                        return config
                    })
                    .catch((err) => {
                        return Promise.reject(err)
                    })
            }

            // Zahaj refresh
            isRefreshing = true

            try {
                const refreshClient = axios.create({ baseURL: API_BASE_URL })
                const response = await refreshClient.post<TokenResponse>(
                    '/api/auth/refresh',
                    { refreshToken }
                )

                const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data
                setTokens(newAccessToken, newRefreshToken)

                processQueue(null, newAccessToken)
                config.headers.Authorization = `Bearer ${newAccessToken}`

                isRefreshing = false
                return config
            } catch (error) {
                processQueue(error, null)
                clearTokens()
                window.location.href = '/login'
                isRefreshing = false
                return Promise.reject(error)
            }
        }

        // Přidej access token do hlavičky
        if (accessToken) {
            config.headers.set('Authorization', `Bearer ${accessToken}`)
        }

        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor - rozbalení backend wrapperu a zpracování chyb
apiClient.interceptors.response.use(
    (response) => {
        // Backend vrací data ve formátu { success: true, data: {...}, meta?: {...} }
        // Rozbalíme data pro snadnější použití, ALE pouze pokud odpověď není paginovaná
        if (response.data && typeof response.data === 'object' && 'data' in response.data) {
            // Pokud je přítomno pole meta s paginačními informacemi (page, total), zachováme celou strukturu
            const hasPaginationMeta = response.data.meta && 
                                     typeof response.data.meta === 'object' && 
                                     ('page' in response.data.meta || 'total' in response.data.meta)
            
            if (!hasPaginationMeta) {
                response.data = response.data.data
            }
        }
        return response
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

        // Pokud je 401 a není to refresh endpoint, zkus refresh token
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true

            const refreshToken = getRefreshToken()

            if (!refreshToken) {
                clearTokens()
                window.location.href = '/login'
                return Promise.reject(error)
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                })
                    .then((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`
                        return apiClient(originalRequest)
                    })
                    .catch((err) => {
                        return Promise.reject(err)
                    })
            }

            isRefreshing = true

            try {
                const refreshClient = axios.create({ baseURL: API_BASE_URL })
                const response = await refreshClient.post<TokenResponse>(
                    '/api/auth/refresh',
                    { refreshToken }
                )

                const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data
                setTokens(newAccessToken, newRefreshToken)

                processQueue(null, newAccessToken)
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`

                isRefreshing = false
                return apiClient(originalRequest)
            } catch (refreshError) {
                processQueue(refreshError, null)
                clearTokens()
                window.location.href = '/login'
                isRefreshing = false
                return Promise.reject(refreshError)
            }
        }

        return Promise.reject(error)
    }
)
